/**
 * Impact Effect Presets
 * 
 * Pre-configured effect combinations for common gameplay scenarios.
 */

import type { RippleConfig, ShakeConfig } from './types';

export interface ImpactPreset {
  /** Display name for UI/debugging */
  name: string;
  
  /** Ripple configuration (null to skip ripple) */
  ripple: Partial<RippleConfig> | null;
  
  /** Shake configuration (null to skip shake) */
  shake: Partial<ShakeConfig> | null;
}

/**
 * Built-in presets for common impact scenarios
 */
export const ImpactPresets = {
  /**
   * Small hit/bump - subtle feedback for minor impacts
   * Good for: light collisions, footsteps, small pickups
   */
  smallImpact: {
    name: 'Small Impact',
    ripple: { 
      amplitude: 0.01, 
      duration: 0.2, 
      frequency: 15,
      speed: 2.0,
      timeRamp: 'punch' as const,
    },
    shake: { 
      amplitude: 0.05, 
      duration: 0.12, 
      frequency: 45,
      zoomAmount: 1.02,
      timeRamp: 'punch' as const,
    },
  },

  /**
   * Medium explosion - standard destruction event
   * Good for: crate destruction, grenades, enemy defeats
   */
  mediumExplosion: {
    name: 'Medium Explosion',
    ripple: { 
      amplitude: 0.035, 
      duration: 0.5, 
      frequency: 10, 
      speed: 1.3,
      timeRamp: 'explosion' as const,
    },
    shake: { 
      amplitude: 1.15, 
      duration: 0.35, 
      frequency: 28,
      zoomAmount: 1.04,
      timeRamp: 'easeOut' as const,
    },
  },

  /**
   * Large explosion - major destruction event
   * Good for: boss defeats, big explosions, level transitions
   */
  largeExplosion: {
    name: 'Large Explosion',
    ripple: { 
      amplitude: 0.06, 
      duration: 0.7, 
      frequency: 6, 
      speed: 1.0,
      timeRamp: 'explosion' as const,
    },
    shake: { 
      amplitude: 0.25, 
      duration: 0.5, 
      frequency: 20, 
      zoomAmount: 1.08,
      timeRamp: 'easeOut' as const,
    },
  },

  /**
   * Power-up pickup - celebratory, positive feel
   * Good for: collectibles, power-ups, achievements
   */
  powerUp: {
    name: 'Power-Up',
    ripple: { 
      amplitude: 0.025, 
      duration: 0.4, 
      frequency: 12, 
      speed: 1.8,
      timeRamp: 'easeOut' as const,
    },
    shake: null, // No shake for power-ups - keeps it feeling good
  },

  /**
   * Ground pound / heavy landing
   * Good for: heavy landings, stomps, weight impacts
   */
  groundPound: {
    name: 'Ground Pound',
    ripple: { 
      amplitude: 0.02,
      duration: 0.3,
      frequency: 8,
      speed: 2.5,
      timeRamp: 'punch' as const,
    },
    shake: { 
      amplitude: 0.2, 
      duration: 0.25, 
      frequency: 40, 
      zoomAmount: 1.05,
      timeRamp: 'punch' as const,
    },
  },

  /**
   * Hit reaction - when player takes damage
   * Good for: damage feedback, knockback
   */
  playerHit: {
    name: 'Player Hit',
    ripple: null, // No ripple for hits - keeps focus on action
    shake: { 
      amplitude: 0.12, 
      duration: 0.2, 
      frequency: 35,
      zoomAmount: 1.03,
      timeRamp: 'punch' as const,
    },
  },

  /**
   * Critical hit - extra impactful hit
   * Good for: critical strikes, finishing blows
   */
  criticalHit: {
    name: 'Critical Hit',
    ripple: { 
      amplitude: 0.02, 
      duration: 0.25, 
      frequency: 20,
      speed: 2.5,
      timeRamp: 'punch' as const,
    },
    shake: { 
      amplitude: 0.18, 
      duration: 0.22, 
      frequency: 50,
      zoomAmount: 1.04,
      timeRamp: 'punch' as const,
    },
  },

  /**
   * Shockwave - spreading wave effect without shake
   * Good for: magic effects, energy releases
   */
  shockwave: {
    name: 'Shockwave',
    ripple: { 
      amplitude: 0.04, 
      duration: 0.6, 
      frequency: 4, 
      speed: 0.8,
      timeRamp: 'easeInOut' as const,
    },
    shake: null,
  },

  /**
   * Earthquake - strong sustained shake
   * Good for: environmental effects, boss abilities
   */
  earthquake: {
    name: 'Earthquake',
    ripple: null,
    shake: { 
      amplitude: 0.3, 
      duration: 1.0, 
      frequency: 15, 
      zoomAmount: 1.1,
      timeRamp: 'bounce' as const,
    },
  },
} as const satisfies Record<string, ImpactPreset>;

/**
 * Type for preset names
 */
export type ImpactPresetName = keyof typeof ImpactPresets;

/**
 * Get a preset by name with type safety
 */
export function getPreset(name: ImpactPresetName): ImpactPreset {
  return ImpactPresets[name];
}

/**
 * Get all available preset names
 */
export function getPresetNames(): ImpactPresetName[] {
  return Object.keys(ImpactPresets) as ImpactPresetName[];
}
